export function initAccount(scope = document) {
  const root = scope && scope.querySelector ? scope : document;
  const container = root.querySelector('#account-orders');
  if (!container) return;
  if (container.__accountInited) return;
  container.__accountInited = true;

  // ===== Tabs =====
  const links = Array.from(container.querySelectorAll('.js-tab-link, .nav-link[data-target]'));
  const panes = Array.from(container.querySelectorAll('.tab-pane'));

  function setActive(target) {
    if (!target || target.charAt(0) !== '#') return;

    links.forEach((l) => {
      const t = l.getAttribute('data-target') || l.getAttribute('href');
      const on = t === target;
      l.classList.toggle('active', on);
      l.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    panes.forEach((p) => {
      const on = `#${p.id}` === target;
      p.classList.toggle('active', on);
      p.classList.toggle('show', on);
      p.setAttribute('aria-hidden', on ? 'false' : 'true');
    });
  }

  container.addEventListener('click', (e) => {
    const a = e.target.closest('.js-tab-link, .nav-link[data-target]');
    if (!a) return;
    const target = a.getAttribute('data-target') || a.getAttribute('href');
    if (!target || !target.startsWith('#')) return;

    e.preventDefault();
    setActive(target);
    if (history && history.replaceState) history.replaceState(null, '', target);

    // Render wishlist when tab is opened (so it never "looks empty" due to timing)
    if (target === '#tab-wishlist') renderWishlist(container);
  });

  const allowed = ['#tab-orders', '#tab-addresses', '#tab-wishlist'];
  const initial = allowed.includes(location.hash) ? location.hash : '#tab-orders';
  setActive(initial);

  wireOrdersSorting(container);
  wireModalsPolyfill(container);
  wireCountryRequiredValidation(container);
  wireAddressDelete(container);

  // Render wishlist on load if the tab is already active (hash or server-side default)
  if (initial === '#tab-wishlist') renderWishlist(container);
}

// ===== Sort orders by date =====
function wireOrdersSorting(container) {
  const tab = container.querySelector('#tab-orders');
  if (!tab) return;

  const sortBtn = tab.querySelector('.sort-date');
  if (!sortBtn || sortBtn.__bound) return;
  sortBtn.__bound = true;

  const table = tab.querySelector('#orders-table');
  const mobileWrap = tab.querySelector('#orders-mobile-list');

  function sortNodesByDate(nodes, dir) {
    const arr = Array.prototype.slice.call(nodes);
    arr.sort((a, b) => {
      const da = Number(a.getAttribute('data-date')) || 0;
      const db = Number(b.getAttribute('data-date')) || 0;
      return dir === 'asc' ? da - db : db - da;
    });
    return arr;
  }

  function applySort(dir) {
    if (table) {
      const tbody = table.querySelector('tbody');
      if (tbody) {
        const rows = tbody.querySelectorAll('tr[data-date]');
        sortNodesByDate(rows, dir).forEach((r) => tbody.appendChild(r));
      }
    }

    if (mobileWrap) {
      const cards = mobileWrap.querySelectorAll('[data-date]');
      sortNodesByDate(cards, dir).forEach((c) => mobileWrap.appendChild(c));
    }

    sortBtn.dataset.dir = dir;
    sortBtn.setAttribute('aria-sort', dir === 'asc' ? 'ascending' : 'descending');

    const icon = sortBtn.querySelector('.sort-date-icon');
    if (icon) {
      icon.style.transition = 'transform .2s ease';
      icon.style.transform = dir === 'asc' ? 'rotate(180deg)' : 'rotate(0deg)';
    }
  }

  sortBtn.addEventListener('click', () => {
    const current = sortBtn.getAttribute('data-dir') || 'desc';
    const next = current === 'desc' ? 'asc' : 'desc';
    applySort(next);
  });

  applySort(sortBtn.getAttribute('data-dir') || 'desc');
}

// ===== Modal polyfill (if Bootstrap JS is not loaded) =====
function wireModalsPolyfill(container) {
  const hasBootstrapModal = !!(window.bootstrap && window.bootstrap.Modal);
  let activeBackdrop = null;

  function openModal(sel) {
    const m = document.querySelector(sel);
    if (!m) return;

    m.classList.add('show');
    m.style.display = 'block';
    m.removeAttribute('aria-hidden');
    m.setAttribute('aria-modal', 'true');

    if (!activeBackdrop) {
      activeBackdrop = document.createElement('div');
      activeBackdrop.className = 'modal-backdrop fade show';
      document.body.appendChild(activeBackdrop);
    }

    document.body.classList.add('modal-open');

    const focusable = m.querySelector(
      '[data-bs-dismiss="modal"], .btn-close, button, [href], input, select, textarea'
    );
    (focusable || m).focus();
  }

  function closeModal(modalEl) {
    if (!modalEl) return;

    modalEl.classList.remove('show');
    modalEl.style.display = 'none';
    modalEl.setAttribute('aria-hidden', 'true');
    modalEl.removeAttribute('aria-modal');

    if (activeBackdrop && activeBackdrop.parentNode) {
      activeBackdrop.parentNode.removeChild(activeBackdrop);
    }
    activeBackdrop = null;

    document.body.classList.remove('modal-open');
  }

  if (!hasBootstrapModal) {
    document.addEventListener('click', function (e) {
      const opener = e.target.closest('[data-bs-toggle="modal"]');
      if (opener) {
        e.preventDefault();
        const target = opener.getAttribute('data-bs-target') || opener.getAttribute('href');
        if (target && target.startsWith('#')) openModal(target);
        return;
      }

      const closer = e.target.closest('[data-bs-dismiss="modal"], .btn-close');
      if (closer) {
        const modalEl = closer.closest('.modal');
        closeModal(modalEl);
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        const open = document.querySelector('.modal.show');
        if (open) closeModal(open);
      }
    });
  } else {
    document.addEventListener('click', function (e) {
      const closer = e.target.closest('[data-bs-dismiss="modal"], .btn-close');
      if (!closer) return;

      const modalEl = closer.closest('.modal');
      if (!modalEl) return;

      const inst = window.bootstrap.Modal.getInstance(modalEl);
      if (inst) inst.hide();
    });
  }
}

// ===== Country required (Shopify address forms) =====
function wireCountryRequiredValidation(container) {
  const countrySelects = document.querySelectorAll(
    '#modalAddAddress select[name="address[country]"], [id^="modalEditAddress_"] select[name="address[country]"]'
  );

  countrySelects.forEach((sel) => {
    sel.setAttribute('required', 'required');

    const firstOpt = sel.options[0];
    const hasEmptyFirst = firstOpt && firstOpt.value === '';

    if (!hasEmptyFirst) {
      const ph = document.createElement('option');
      ph.value = '';
      ph.textContent = 'Select your country';
      ph.disabled = true;
      ph.selected = true;
      sel.insertBefore(ph, sel.firstChild);
    } else {
      if (!sel.value) sel.selectedIndex = 0;
    }

    sel.addEventListener('invalid', function () {
      this.setCustomValidity('Please select your country.');
    });

    sel.addEventListener('change', function () {
      this.setCustomValidity('');
    });
  });

  document.addEventListener('submit', function (e) {
    const form = e.target;
    if (!form || !/\/account\/addresses/.test(form.action || '')) return;

    const country = form.querySelector('select[name="address[country]"]');
    if (!country) return;

    if (!country.value) {
      e.preventDefault();
      if (country.reportValidity) country.reportValidity();
      else alert('Please select your country.');
      country.focus();
      return;
    }

    // Ensure the "Addresses" tab stays active after redirect
    try {
      sessionStorage.setItem('activateAddressesTab', '1');
    } catch (_) {}
  });

  (function ensureAddressesTab() {
    let wantAddresses = false;
    try {
      wantAddresses = sessionStorage.getItem('activateAddressesTab') === '1';
      sessionStorage.removeItem('activateAddressesTab');
    } catch (_) {}

    if (!wantAddresses) return;

    const target = '#tab-addresses';
    const links = container.querySelectorAll('.js-tab-link, .nav-link[data-target]');
    const panes = container.querySelectorAll('.tab-pane');

    panes.forEach((p) => {
      const on = `#${p.id}` === target;
      p.classList.toggle('active', on);
      p.classList.toggle('show', on);
      p.setAttribute('aria-hidden', on ? 'false' : 'true');
    });

    links.forEach((l) => {
      const t = l.getAttribute('data-target') || l.getAttribute('href');
      const on = t === target;
      l.classList.toggle('active', on);
      l.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    if (history && history.replaceState) history.replaceState(null, '', target);
  })();
}

// ===== Delete address (Shopify) =====
function wireAddressDelete(container) {
  const form = document.getElementById('delete-address-form');
  if (!form) return;

  const base = (form.getAttribute('data-base') || '').replace(/\/$/, '');
  if (!base) return;

  container.querySelectorAll('.delete-address-btn').forEach((btn) => {
    if (!btn.getAttribute('type')) btn.setAttribute('type', 'button');
  });

  container.addEventListener('click', (e) => {
    const btn = e.target.closest('.delete-address-btn');
    if (!btn) return;

    e.preventDefault();
    const id = btn.getAttribute('data-address-id');
    if (!id) return;

    const ok = window.confirm('Are you sure you want to delete this address?');
    if (!ok) return;

    form.setAttribute('action', `${base}/${id}`);
    form.submit();
  });
}

// ===================== WISHLIST (FIXED FOR 2 KEYS) =====================

const WISHLIST_CANONICAL_KEY = 'wishlist:products';
const WISHLIST_LEGACY_KEYS = ['wishlist_free_v1', 'wishlist:products'];

/* Merges legacy keys into one canonical key so the account page never shows empty again */
function getMergedWishlistHandles() {
  const merged = new Set();

  WISHLIST_LEGACY_KEYS.forEach((key) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      // ["handle-1", "handle-2"]
      if (Array.isArray(parsed)) {
        parsed.forEach((h) => {
          const v = String(h || '').trim();
          if (v) merged.add(v);
        });
        return;
      }

      // { "handle": true }
      if (parsed && typeof parsed === 'object') {
        Object.keys(parsed).forEach((k) => {
          if (parsed[k]) merged.add(String(k).trim());
        });
      }
    } catch (_) {
      // Ignore invalid JSON
    }
  });

  return Array.from(merged);
}

/* Writes handles back to the canonical key and optionally cleans up legacy keys */
function persistWishlist(handles, cleanupLegacy = true) {
  try {
    localStorage.setItem(WISHLIST_CANONICAL_KEY, JSON.stringify(handles));
  } catch (_) {}

  if (!cleanupLegacy) return;

  // Keep only the canonical key to avoid future mismatch
  WISHLIST_LEGACY_KEYS.forEach((k) => {
    if (k !== WISHLIST_CANONICAL_KEY) {
      try {
        localStorage.removeItem(k);
      } catch (_) {}
    }
  });
}

function removeFromWishlist(handle) {
  const current = getMergedWishlistHandles();
  const next = current.filter((h) => h !== handle);
  persistWishlist(next, true);
  return next;
}

function renderWishlist(container) {
  const tab = container.querySelector('#tab-wishlist');
  if (!tab) return;

  const grid = tab.querySelector('#wishlist-grid');
  const empty = tab.querySelector('#wishlist-empty');
  if (!grid || !empty) return;

  // Always merge keys on load so we normalize data once
  const handles = getMergedWishlistHandles();
  persistWishlist(handles, true);

  // Bind remove events only once
  if (!tab.__wishlistBound) {
    tab.__wishlistBound = true;

    tab.addEventListener('click', (e) => {
      const btn = e.target.closest('.js-wishlist-remove');
      if (!btn) return;

      e.preventDefault();
      const handle = btn.getAttribute('data-handle');
      if (!handle) return;

      const next = removeFromWishlist(handle);

      const col = btn.closest('.col');
      if (col) col.remove();

      if (!next.length) {
        grid.innerHTML = '';
        empty.classList.remove('d-none');
      }
    });
  }

  grid.innerHTML = '';
  empty.classList.add('d-none');

  if (!handles.length) {
    empty.classList.remove('d-none');
    return;
  }

  grid.innerHTML = `<div class="col-12"><p class="text-second">Loading wishlist...</p></div>`;

  fetchWishlistProducts(handles)
    .then((products) => {
      grid.innerHTML = '';

      if (!products.length) {
        empty.classList.remove('d-none');
        return;
      }

      grid.innerHTML = products.map(wishlistCardHTML).join('');
    })
    .catch(() => {
      grid.innerHTML = '';
      empty.classList.remove('d-none');
    });
}

async function fetchWishlistProducts(handles) {
  const out = [];
  for (const handle of handles) {
    try {
      const p = await fetchProductByHandle(handle);
      if (p) out.push(p);
    } catch (_) {}
  }
  return out;
}

async function fetchProductByHandle(handle) {
  const url = `/products/${encodeURIComponent(handle)}.js`;
  const res = await fetch(url, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Failed to fetch product: ${handle}`);
  return await res.json();
}

function wishlistCardHTML(product) {
  const title = product?.title || '';
  const handle = product?.handle || '';
  const url = handle ? `/products/${handle}` : '#';

  // Image (keep same approach)
  const img =
    Array.isArray(product?.images) && product.images.length
      ? product.images[0]
      : '';

  // Price
  const activeCurrency =
    (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
  const price = moneyFromCents(product?.price || 0, activeCurrency, 'nl-NL');

  // Variant label (best-effort from .js endpoint)
  const variantLabel =
    (product?.variants && product.variants[0] && product.variants[0].public_title) ||
    (product?.variants && product.variants[0] && product.variants[0].title) ||
    '';

  return `
    <div class="col">
      <div class="card kmf-card h-100">
        <a href="${url}" class="card-img-top">
          <div class="card-media">
            ${img ? `<img src="${img}" alt="${escapeAttr(title)}" loading="lazy">` : ``}
          </div>
        </a>

        <div class="card-body d-flex flex-column">
          <h3 class="card-title text-normal mb-0">
            ${escapeHtml(title)}
          </h3>

          <p class="pcar-card__variant text-tirty fw-normal mb-2">
            ${escapeHtml(variantLabel)}
          </p>

          <div class="text-large kmf-price">${escapeHtml(price)}</div>

          <!-- Optional remove button (if you want it inside this card layout) -->
          <button
            type="button"
            class="cta cta-second mt-3 js-wishlist-remove"
            data-handle="${escapeAttr(handle)}"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  `;
}


function moneyFromCents(cents, currency = 'EUR', locale = 'nl-NL') {
  const value = (Number(cents) || 0) / 100;
  const isFrench = (document.documentElement.lang || window.Shopify?.locale || '').toLowerCase().startsWith('fr');
  if (isFrench) {
    return `${value.toFixed(2).replace('.', ',')} €`;
  }
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  } catch (_) {
    return `€${value.toFixed(2)}`;
  }
}

function escapeHtml(str) {
  return String(str || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');
}

function escapeAttr(str) {
  return escapeHtml(str).replaceAll('`', '&#096;');
}
